import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  obtenerCategorias,
  obtenerProductos,
  crearCategoria,
  crearProducto,
  actualizarProducto,
  eliminarProducto,
} from "../redux/actions/index";
import SweetAlert from "sweetalert2";
import ProductForm from "./ProductoForm";

const AltaProd = () => {
  const dispatch = useDispatch();
  const productos = useSelector((state) => state.productos);
  const categorias = useSelector((state) => state.categorias);
  const [busqueda, setBusqueda] = useState("");

  const [selectedProduct, setSelectedProduct] = useState(null);

  // Manejar selección de producto
  const handleProductSelection = (product) => {
    setSelectedProduct(product);
  };

  // Filtrar los productos según la búsqueda
  const productosFiltrados = productos.filter((producto) => {
    const busquedaLower = busqueda.toLowerCase();
    return (
      producto.nombre.toLowerCase().includes(busquedaLower) ||
      producto.codigoBarras.includes(busquedaLower)
    );
  });

  // Ordenar los productos por stock y alfabéticamente
  const productosOrdenados = [...productosFiltrados].sort((a, b) => {
    // Ordenar por stock en orden descendente
    if (b.stock !== a.stock) {
      return b.stock - a.stock;
    }
    // Si el stock es igual, ordenar alfabéticamente por nombre
    return a.nombre.localeCompare(b.nombre);
  });

  // Crear o actualizar producto
  const handleSaveProduct = async (productData) => {
    try {
      let categoriaId = productData.categoriaId;
      // Crear nueva categoría si es necesario
      if (productData.nuevaCategoria) {
        await dispatch(crearCategoria({ nombre: productData.nuevaCategoria }))
          .then((categoriaCreada) => {
            categoriaId = categoriaCreada.id; // crearCategoria devuelve la categoría creada
          })
          .catch((error) => {
            throw new Error(
              "Error al crear la nueva categoría: " +
                (error.response?.data?.mensaje || error.message)
            );
          });
      }

      // Crear o actualizar producto
      if (selectedProduct) {
        // Actualizar producto existente
        await dispatch(
          actualizarProducto({
            ...productData,
            id: selectedProduct.id,
            categoriaId,
          })
        )
          .then(() => {
            SweetAlert.fire(
              "Éxito",
              "Producto actualizado correctamente",
              "success"
            );
            dispatch(obtenerProductos());
          })
          .catch((error) => {
            throw new Error(
              "Error al actualizar el producto: " +
                (error.response?.data?.mensaje || error.message)
            );
          });
      } else {
        // Crear nuevo producto
        await dispatch(
          crearProducto({
            ...productData,
            categoriaId,
          })
        )
          .then(() => {
            SweetAlert.fire(
              "Éxito",
              "Producto creado correctamente",
              "success"
            );
          })
          .catch((error) => {
            throw new Error(
              "Error al crear el producto: " +
                (error.response?.data?.mensaje || error.message)
            );
          });
      }

      // Resetear selección y formulario
      setSelectedProduct(null);
    } catch (error) {
      SweetAlert.fire("Error", error.message, "error");
    }
  };

  useEffect(() => {
    dispatch(obtenerProductos());
    dispatch(obtenerCategorias());
  }, []);

  return (
    <div className="grid p-4 bg-backgroundClaro grid-cols-1 md:grid-cols-2 h-full w-full gap-4">
      <ProductForm
        onSave={handleSaveProduct}
        selectedProduct={selectedProduct}
        categorias={categorias}
      />
      <div className="bg-backgroundOscuro rounded-lg">
        <h1 className="flex text-textoClaro justify-center text-4xl font-bold">
          Listado de Productos
        </h1>
        <div className="mb-4">
          <input
            type="text"
            className="bg-backgroundClaro border-[#000000] rounded-lg text-textoOscuro placeholder-[#808080] focus:ring-4 focus:ring-green-800 focus:border-green-600 w-[100%] p-2 sm:p-1"
            placeholder="Buscar por nombre o ID"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>

        {productosOrdenados?.length ? (
          <div className="mx-auto flex w-full flex-col justify-center px-5 pt-0 md:h-[unset] md:max-w-[95%] mt-10">
            <div className="h-full w-full rounded-lg">
              <div className="rounded-lg border bg-card text-card-foreground shadow-sm h-full w-full border-zinc-200 p-0 dark:border-zinc-800 sm:overflow-auto">
                <div className="overflow-x-scroll xl:overflow-x-hidden">
                  <div className="relative w-full overflow-auto">
                    <table className="caption-bottom text-sm w-full">
                      <thead className="[&_tr]:border-b border-b-[1px] p-6 border-zinc-800">
                        <tr className="border-zinc-800">
                          <th className="h-12 border-r-2 px-4 align-middle text-white font-medium text-muted-foreground cursor-pointer border-zinc-200 pl-5 pr-4 pt-2 text-start dark:border-zinc-800">
                            Nombre Producto
                          </th>
                          <th className="h-12 border-r-2 px-4 align-middle text-white font-medium text-muted-foreground cursor-pointer border-zinc-200 pl-5 pr-4 pt-2 text-start dark:border-zinc-800">
                            Categoría
                          </th>
                          <th className="h-12 border-r-2 px-4 align-middle text-white font-medium text-muted-foreground cursor-pointer border-zinc-200 pl-5 pr-4 pt-2 text-start dark:border-zinc-800">
                            Cantidad
                          </th>
                          <th className="h-12 border-r-2 px-4 align-middle text-white font-medium text-muted-foreground cursor-pointer border-zinc-200 pl-5 pr-4 pt-2 text-start dark:border-zinc-800">
                            Unidad de Medida
                          </th>
                          <th className="h-12 px-4 align-middle text-white font-medium text-muted-foreground cursor-pointer border-zinc-200 pl-5 pr-4 pt-2 text-start dark:border-zinc-800"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {productosOrdenados?.map((e) => (
                          <tr
                            key={e.id}
                            className="border-b transition-colors hover:bg-muted/50  dark:hover:bg-gray-900"
                          >
                            <td className="p-4 border-r-2 align-middle w-max border-b-[1px] text-white border-zinc-200 py-5 pl-5 pr-4 dark:border-white/10">
                              {e.nombre}
                            </td>
                            <td className="p-4 border-r-2 align-middle w-max border-b-[1px] text-white border-zinc-200 py-5 pl-5 pr-4 dark:border-white/10">
                              {e.categoria?.nombre || "Sin categoría"}
                            </td>
                            <td className="p-4 border-r-2 align-middle w-max border-b-[1px] text-white border-zinc-200 py-5 pl-5 pr-4 dark:border-white/10">
                              {e.stock}
                            </td>
                            <td className="p-4 border-r-2 align-middle w-max border-b-[1px] text-white border-zinc-200 py-5 pl-5 pr-4 dark:border-white/10">
                              {e.unidadMedida}
                            </td>
                            <td className="p-4 border-r-2 align-middle w-max border-b-[1px] border-zinc-200 py-5 pl-5 pr-4 dark:border-white/10">
                              <button
                                onClick={() => handleProductSelection(e)}
                                className="bg-green-900 flex items-center my-2 p-1 rounded-md text-textoClaro font-bold text-base hover:bg-green-600 shadow-lg shadow-green-500/50"
                              >
                                Seleccionar
                              </button>
                            </td>
                            <td className="p-4 border-r-2 align-middle w-max border-b-[1px] text-white border-zinc-200 py-5 pl-5 pr-4 dark:border-white/10">
                              <button
                                onClick={async () => {
                                  const confirm = await SweetAlert.fire({
                                    title: "¿Estás seguro?",
                                    text: "Esta acción no se puede deshacer",
                                    icon: "warning",
                                    showCancelButton: true,
                                    confirmButtonText: "Sí, eliminar",
                                    cancelButtonText: "Cancelar",
                                  });

                                  if (confirm.isConfirmed) {
                                    try {
                                      await dispatch(eliminarProducto(e.id));
                                      SweetAlert.fire(
                                        "Eliminado",
                                        "Producto eliminado",
                                        "success"
                                      );
                                      dispatch(obtenerProductos())
                                    } catch (error) {
                                      SweetAlert.fire(
                                        "Error",
                                        error.message,
                                        "error"
                                      ); // ⬅️ Mostrás el mensaje recibido
                                    }
                                  }
                                }}
                                className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                              >
                                Eliminar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-white text-center">No hay productos disponibles</p>
        )}
      </div>
    </div>
  );
};

export default AltaProd;